(() => {
	'use strict';

	const THRESHOLD = 0.5;

	const DIRECTION = {
		horizontal: 'horizontal',
		horizontalReversed: 'horizontal-reversed',
		vertical: 'vertical',
		verticalReversed: 'vertical-reversed',
	};

	const INTERSECTING_TYPE = {
		maxVisibility: 'maximum-visibility-of-each-image-in-viewport',
		middleOfViewport: 'image-in-the-middle-of-viewport',
	};

	if (customElements.get('sticky-images-and-text')) return;

	customElements.define(
		'sticky-images-and-text',
		class StickyImagesAndText extends HTMLElement {
			constructor() {
				super();

				this.performAnimation = null;
				this.lastOffset = 0;
				this.activeIndex = 0;
				this.lastActiveIndex = this.activeIndex;
				this.lastActiveImageHeight = 0;

				// Prevents flapping near a boundary when offsets jitter (e.g. due to layout updates).
				// Value is in "index units" (0..1). 0.12 means you must pass 62% into the next slide.
				this.indexHysteresis = 0.12;

				// Scroll-based carousel properties
				this.scrollThreshold = 100; // Pixels to scroll before changing slide
				this.scrollAccumulator = 0;
				this.lastScrollTime = Date.now();
				this.scrollTimeout = null;

				this.onScrollCallback = (e) => this.onScroll(e);
				this.onResizeCallback = () => this.onResize();
				this.calculateImagesOffsetCallback = () => this.calculateImagesOffset();
			}

			activate(index) {
				const maxIndex = this.image?.length ? this.image.length - 1 : 0;
				const next = Math.max(0, Math.min(Number(index) || 0, maxIndex));
				this.activeIndex = next;
				this.lastActiveIndex = next;
				this.sync();
			}

			connectedCallback() {
				this.scrollDirection = this.dataset.scrollDirection;
				this.horizontalScroll =
					this.scrollDirection === DIRECTION.horizontal ||
					this.scrollDirection === DIRECTION.horizontalReversed;
				this.verticalScroll =
					this.scrollDirection === DIRECTION.vertical ||
					this.scrollDirection === DIRECTION.verticalReversed;
				this.reverseDirection =
					this.scrollDirection === DIRECTION.horizontalReversed ||
					this.scrollDirection === DIRECTION.verticalReversed;

				this.direction = this.reverseDirection ? 1 : -1;
				this.withGaps = this.hasAttribute('data-sticky-with-gaps');
				this.singleText = this.hasAttribute('data-sticky-single-text');

				this.image = this.querySelectorAll('.js-only [data-sticky-image]');
				this.text = this.querySelectorAll('.js-only [data-sticky-text]');
				this.textInner = this.querySelectorAll('.js-only [data-sticky-text-inner]');
				this.images = this.querySelector('[data-sticky-images]');
				this.dot = this.querySelectorAll('[data-sticky-dot]');
				this.aside = this.querySelector('[data-sticky-aside]');
				this.inner = this.querySelector('[data-sticky-inner]');
				this.content = this.querySelector('[data-sticky-content]');
				this.wrapper = this.querySelector('[data-sticky-wrapper]');
				this.textHighlight = this.querySelectorAll('text-highlight');


				if (!this.wrapper) return;

				// Slides are controlled by scroll-based carousel
				this.calculateHeights();
				this.syncTextToImageHeight();

				this.removeAnimationFrame();

				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						this.calculateHeights();
						this.syncTextToImageHeight();
					});
				});

				document.addEventListener('theme:resize:width', this.onResizeCallback);
				window.addEventListener('wheel', this.onScrollCallback, { passive: true });

				this.syncDots();
				this.observeResize();
				this.activate(this.activeIndex);
			}

			getTypeOfIntersecting() {
				this.typeOfIntersecting = INTERSECTING_TYPE.maxVisibility;
				this.fractionOfViewport = this.image[0].offsetHeight / window.innerHeight;

				if (this.verticalScroll && !window.theme.isMobile() && this.fractionOfViewport < 0.6) {
					this.typeOfIntersecting = INTERSECTING_TYPE.middleOfViewport;
				}
			}

			observeImagesIntersecting() {
				this.observersCollection = new Set();
				this.compareIntersectionRatio = [];
				this.maxVisibility = 0;

				this.observer = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							const imageHeight = entry.target.offsetHeight;
							const isIntersecting = entry.isIntersecting;

							const data = {
								target: entry.target,
								intersectionRatio: entry.intersectionRatio,
							};

							const useMaxVisibility =
								this.typeOfIntersecting === INTERSECTING_TYPE.maxVisibility;

							// Note: original theme code had an unused threshold tweak when image is taller than viewport.
							// Keeping behavior equivalent by not changing the observer thresholds.
							void imageHeight;

							if (useMaxVisibility && isIntersecting) {
								this.observersCollection.forEach((item) => {
									if (item.target === entry.target) this.observersCollection.delete(item);
								});

								this.observersCollection.add(data);

								this.compareIntersectionRatio = [...this.observersCollection].map(
									(item) => item.intersectionRatio,
								);

								this.maxVisibility = Math.max(...this.compareIntersectionRatio);
							}
						});
					},
					{ root: null, rootMargin: '0px', threshold: [0, 1] },
				);

				this.image.forEach((img) => this.observer.observe(img));
			}

			onScroll(event) {
				// Only activate when the section's vertical center is near the viewport center
				const rect = this.getBoundingClientRect();
				const viewportCenter = window.innerHeight / 2;
				const sectionCenter = rect.top + rect.height / 2;

				// Section center must be within the middle third of the viewport
				const isCentered = sectionCenter > window.innerHeight * 0.25 && sectionCenter < window.innerHeight * 0.75;

				if (!isCentered) {
					this.scrollAccumulator = 0;
					return;
				}

				// Get scroll delta from wheel event
				const delta = event.deltaY;

				// Accumulate scroll
				this.scrollAccumulator += delta;

				// Clear existing timeout
				if (this.scrollTimeout) {
					clearTimeout(this.scrollTimeout);
				}

				// Check if we've exceeded the threshold
				if (Math.abs(this.scrollAccumulator) >= this.scrollThreshold) {
					const maxIndex = this.image?.length ? this.image.length - 1 : 0;

					if (this.scrollAccumulator > 0) {
						// Scrolling down - next slide
						if (this.activeIndex < maxIndex) {
							this.activate(this.activeIndex + 1);
						}
					} else {
						// Scrolling up - previous slide
						if (this.activeIndex > 0) {
							this.activate(this.activeIndex - 1);
						}
					}

					// Reset accumulator after slide change
					this.scrollAccumulator = 0;
				}

				// Reset accumulator after inactivity (500ms)
				this.scrollTimeout = setTimeout(() => {
					this.scrollAccumulator = 0;
				}, 500);
			}

			removeAnimationFrame() {
				if (!this.performAnimation) return;
				cancelAnimationFrame(this.performAnimation);
				this.performAnimation = null;
			}

			checkImagePositionInViewport(img) {
				if (!img) return;

				const windowHeight = window.innerHeight;
				const scrollTop = window.scrollY;
				const middleOfViewport = scrollTop + windowHeight / 2;
				const bottomOfViewport = scrollTop + windowHeight;

				const top = Math.round(img.getBoundingClientRect().top + scrollTop);
				const bottom = top + img.offsetHeight;

				const isInViewport = top < bottomOfViewport && !(bottom < scrollTop);
				const isMiddleInside = middleOfViewport > top && bottom > middleOfViewport;

				if (isInViewport && isMiddleInside) {
					this.activeIndex = Number(img.dataset.index);
					if (this.lastActiveIndex !== this.activeIndex) {
						this.sync();
						this.lastActiveIndex = this.activeIndex;
					}
				}
			}

			calculateImagesOffset() {
				const asideTop = this.aside.getBoundingClientRect().top - this.headerHeight;
				const stickyTopRelative = asideTop - this.innerStickyTop;

				const offsetBase = this.hasShortImages ? stickyTopRelative : asideTop;

				let percent = 0;
				if (offsetBase < 0) percent = (offsetBase / this.bottomEndingPoint) * -1 * 100;
				else if (offsetBase >= 0) percent = 0;
				else percent = 100;

				percent = percent > 100 ? 100 : percent;
				percent *= this.image.length - 1;

				const floatIndex = percent / 100;

				// Stabilize index selection near boundaries.
				const candidate = Math.round(floatIndex);
				if (candidate !== this.activeIndex) {
					const distance = Math.abs(floatIndex - this.activeIndex);
					if (distance > THRESHOLD + this.indexHysteresis) {
						this.activeIndex = candidate;
					}
				}

				if (this.lastActiveIndex !== this.activeIndex) {
					this.sync();
					this.lastActiveIndex = this.activeIndex;
				}

				if (this.lastOffset !== percent) {
					this.performAnimation = requestAnimationFrame(this.calculateImagesOffsetCallback);
				} else if (this.performAnimation) {
					this.removeAnimationFrame();
				}

				this.lastOffset = percent;
			}

			calculateHeights() {
				const { stickyHeaderHeight, windowHeight } = window.theme.readHeights();

				this.headerHeight = stickyHeaderHeight || 0;
				this.windowHeight = windowHeight || window.innerHeight;

				if (this.text.length > 0) {
					const paddingTop = this.content
						? parseInt(
								window
									.getComputedStyle(this.content)
									.getPropertyValue('padding-top'),
								10,
							)
						: 0;
					const paddingBottom = this.content
						? parseInt(
								window
									.getComputedStyle(this.content)
									.getPropertyValue('padding-bottom'),
								10,
							)
						: 0;

					const textHeightsWithPadding = [...this.text].map(
						(node) => node.offsetHeight + paddingTop + paddingBottom,
					);
					const innerTextHeights = [...this.textInner].map((node) => node.offsetHeight);

					const highestText = Math.max(...textHeightsWithPadding);
					const highestInnerText = Math.max(...innerTextHeights);

					const highest = window.theme.isMobile() ? highestInnerText : highestText;

					const total = textHeightsWithPadding.reduce((sum, h) => sum + h, 0);
					const average =
						Math.floor(total / textHeightsWithPadding.length) || this.text[0].offsetHeight;

					this.style.setProperty('--average-text-height', `${average}px`);
					this.style.setProperty('--highest-text', `${highest}px`);
				}

				this.imagesHeight = this.images.offsetHeight;
				this.hasShortImages = false;

				if (this.image.length > 0) {
					const wrapper =
						this.image[0].querySelector('.image-wrapper') ||
						this.image[0].querySelector('.sticky-images-and-text__video-wrapper');

					this.imgHeight = wrapper ? wrapper.offsetHeight : 0;
					this.imgPadding = parseInt(
						window.getComputedStyle(this.image[0]).getPropertyValue('padding-top'),
						10,
					);

					this.style.setProperty('--images-height', `${this.imagesHeight}px`);
					this.style.setProperty('--img-height', `${this.imgHeight + 2 * this.imgPadding}px`);
				}

				this.asideHeight = this.aside.offsetHeight;
				this.asidePadding = parseInt(
					window.getComputedStyle(this.aside).getPropertyValue('padding-top'),
					10,
				);

				this.breathingSpace = (this.windowHeight - this.imagesHeight) / 2 / 2;
				this.innerStickyTop =
					parseInt(window.getComputedStyle(this.inner).getPropertyValue('top'), 10) -
					this.headerHeight -
					this.breathingSpace;

				this.gutters = this.withGaps ? 2 * this.asidePadding : 0;

				this.additionalSpace = window.theme.isMobile()
					? this.withGaps
						? this.asidePadding + 10
						: 10
					: this.gutters;

				this.minHeightForEndingPoing = this.imagesHeight + this.additionalSpace;

				if (!window.theme.isMobile() && this.horizontalScroll) {
					this.minHeightForEndingPoing = this.windowHeight - this.headerHeight;

					this.hasShortImages = this.fractionOfViewport <= 0.6;
					if (this.hasShortImages) {
						this.minHeightForEndingPoing =
							this.imagesHeight - this.innerStickyTop + 3 * this.breathingSpace;
					}
				}

				this.bottomEndingPoint = this.asideHeight - this.minHeightForEndingPoing;
			}

			getActiveImageElement() {
				return (
					this.querySelector('.js-only [data-sticky-image].is-active') ||
					this.image?.[Number(this.activeIndex) || 0] ||
					null
				);
			}

			getImageHeightForElement(el) {
				if (!el) return 0;

				const directHeight = Math.round(el.getBoundingClientRect().height);
				if (directHeight > 0) return directHeight;

				const inner =
					el.querySelector('.image-wrapper') ||
					el.querySelector('.sticky-images-and-text__video-wrapper') ||
					el.querySelector('video') ||
					el.firstElementChild;

				const innerHeight = inner ? Math.round(inner.getBoundingClientRect().height) : 0;
				const styles = window.getComputedStyle(el);
				const pt = parseFloat(styles.paddingTop) || 0;
				const pb = parseFloat(styles.paddingBottom) || 0;

				return Math.round(innerHeight + pt + pb);
			}

			syncTextToImageHeight() {
				const activeImage = this.getActiveImageElement();
				const height = this.getImageHeightForElement(activeImage);

				if (height <= 0) return;
				if (Math.abs(height - this.lastActiveImageHeight) <= 1) return;

				this.lastActiveImageHeight = height;
				this.style.setProperty('--active-image-height', `${height}px`);
			}

			observeResize() {
				if (!window.ResizeObserver) return;

				if (this.resizeObserver) this.resizeObserver.disconnect();

				this.resizeObserver = new ResizeObserver(() => {
					this.calculateHeights();
					this.syncTextToImageHeight();
				});

				// Observe containers (not every image) to avoid noisy callbacks during transitions.
				if (this.images) this.resizeObserver.observe(this.images);
				if (this.content) this.resizeObserver.observe(this.content);
			}

			refreshImagesOffsetValues() {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						document.dispatchEvent(new CustomEvent('theme:scroll'));
					});
				});
			}

			onResize() {
				this.calculateHeights();
				this.syncTextToImageHeight();
				requestAnimationFrame(() => {
					this.calculateHeights();
					this.syncTextToImageHeight();
				});
			}

			sync() {
				const textsToToggle = this.singleText ? [] : this.text;

				[...this.image, ...textsToToggle, ...this.dot].forEach((node) => {
					const index = Number(node.getAttribute('data-index'));
					node.classList.toggle('is-active', index === Number(this.activeIndex));
				});

				this.syncTextToImageHeight();
			}

			syncDots() {
				this.dot.forEach((dot) => {
					dot.addEventListener('click', (event) => {
						event.preventDefault();
						const index = Number(event.currentTarget.dataset.index);
						this.activate(index);
					});
				});
			}

			scrollTo(el, delay = 0) {
				if (!el) return;

				const index = Number(el.dataset.index);
				const imageTop = Math.round(el.getBoundingClientRect().top + window.scrollY);
				const imageHeight = el.offsetHeight;
				const wrapperTop = Math.round(this.wrapper.getBoundingClientRect().top + window.scrollY);
				const asideTop = this.aside.getBoundingClientRect().top - this.headerHeight;
				const a = wrapperTop + this.images.offsetHeight * index;

				const isImageTallerThanViewport = Boolean(
					(this.image[0].offsetHeight + this.gutters) /
						(window.innerHeight - this.headerHeight) >=
						1,
				);

				let top = a - this.headerHeight;

				if (window.theme.isMobile()) {
					top =
						asideTop +
						window.scrollY +
						(this.bottomEndingPoint / (this.image.length - 1)) * index;
				}

				if (this.horizontalScroll && window.theme.isDesktop() && !isImageTallerThanViewport) {
					const stickyOffset = asideTop - this.innerStickyTop;
					top =
						(this.hasShortImages ? stickyOffset + window.scrollY : asideTop + window.scrollY) +
						(this.bottomEndingPoint / (this.image.length - 1)) * index;
				}

				if (this.verticalScroll && window.theme.isDesktop()) {
					top = imageTop - this.headerHeight;

					if (this.typeOfIntersecting === INTERSECTING_TYPE.middleOfViewport) {
						top =
							imageTop -
							window.innerHeight / 2 +
							imageHeight / 2 -
							this.headerHeight;
					}
				}

				const params = { top, left: 0, behavior: 'smooth' };
				if (delay > 0) setTimeout(() => window.scrollTo(params), delay);
				else window.scrollTo(params);
			}

			disconnectedCallback() {
				this.image.forEach((img) => this.observer?.unobserve(img));
				this.resizeObserver?.disconnect();

				document.removeEventListener('theme:resize:width', this.onResizeCallback);
				window.removeEventListener('wheel', this.onScrollCallback);

				// Clear scroll timeout
				if (this.scrollTimeout) {
					clearTimeout(this.scrollTimeout);
				}
			}
		},
	);
})();
